import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Assessment,
  CheckCircle,
  Cancel,
  TrendingUp,
  Star,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { resultAPI } from '../../../services/api';

const MyResults = () => {
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState([]);

  useEffect(() => {
    fetchMyResults();
  }, []);

  const fetchMyResults = async () => {
    try {
      setLoading(true);
      const response = await resultAPI.getMyResults();
      setResults(response.data.data?.results || []);
    } catch (error) {
      console.error('Error fetching results:', error);
      toast.error('Failed to load results');
    } finally {
      setLoading(false);
    }
  };

  const getGradeColor = (grade) => {
    if (['A+', 'A'].includes(grade)) return 'success';
    if (['B+', 'B'].includes(grade)) return 'info';
    if (['C', 'D'].includes(grade)) return 'warning';
    return 'error';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          My Results
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View your published examination results
        </Typography>
      </Box>

      {results.length === 0 ? (
        <Alert severity="info" icon={<Assessment />}>
          No published results available yet. Results will appear here once they are published by the admin.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {results.map((result) => (
            <Grid item xs={12} key={result._id}>
              <Card elevation={3}>
                <CardContent>
                  {/* Header Section */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 3 }}>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                        {result.examination?.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Published on: {new Date(result.publishedAt).toLocaleDateString('en-IN', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </Typography>
                    </Box>
                    <Chip
                      label={result.overallResult.toUpperCase()}
                      color={result.overallResult === 'pass' ? 'success' : 'error'}
                      icon={result.overallResult === 'pass' ? <CheckCircle /> : <Cancel />}
                      sx={{ fontWeight: 700, fontSize: '1rem', px: 2, py: 3 }}
                    />
                  </Box>

                  {/* Subject-wise Results */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Subject-wise Results
                    </Typography>
                  </Box>

                  {/* Subject-wise Results Table */}
                  <Box sx={{ mt: 2 }}>
                      <TableContainer component={Paper} elevation={0} sx={{ backgroundColor: 'grey.50' }}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell><strong>Subject</strong></TableCell>
                              <TableCell align="center"><strong>Marks Obtained</strong></TableCell>
                              <TableCell align="center"><strong>Total Marks</strong></TableCell>
                              <TableCell align="center"><strong>Passing Marks</strong></TableCell>
                              <TableCell align="center"><strong>Questions Attempted</strong></TableCell>
                              <TableCell align="center"><strong>Status</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {result.subjectResults?.map((subResult, index) => (
                              <TableRow key={index}>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {subResult.subject?.name || 'N/A'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {subResult.subject?.code || ''}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="body1" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                    {subResult.marksObtained}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">{subResult.totalMarks}</TableCell>
                                <TableCell align="center">{subResult.passingMarks}</TableCell>
                                <TableCell align="center">
                                  <Box>
                                    <Typography variant="body2">{subResult.questionsAttempted}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      ({subResult.correctAnswers} correct, {subResult.incorrectAnswers} incorrect)
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell align="center">
                                  <Chip
                                    label={subResult.isPassed ? 'Pass' : 'Fail'}
                                    size="small"
                                    color={subResult.isPassed ? 'success' : 'error'}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {/* Performance Summary */}
                      <Box sx={{ mt: 3, p: 2, backgroundColor: 'info.lighter', borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <TrendingUp sx={{ color: 'info.main' }} />
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'info.main' }}>
                            Performance Summary
                          </Typography>
                        </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={4}>
                            <Typography variant="body2" color="text.secondary">
                              Total Subjects
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {result.subjectResults?.length || 0}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Typography variant="body2" color="text.secondary">
                              Subjects Passed
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main' }}>
                              {result.subjectResults?.filter(s => s.isPassed).length || 0}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Typography variant="body2" color="text.secondary">
                              Subjects Failed
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: 'error.main' }}>
                              {result.subjectResults?.filter(s => !s.isPassed).length || 0}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    </Box>

                  {/* Remarks */}
                  {result.remarks && (
                    <Box sx={{ mt: 3, p: 2, backgroundColor: 'warning.lighter', borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'warning.dark' }}>
                        Remarks
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {result.remarks}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default MyResults;
